import * as React from 'react';

import classnames from 'classnames';

import {Button} from '../button/button';
import {SlideshowControls} from '../carousel/slideshow-controls';
import {Link} from '../link/link';

import styles from './onboarding-page.module.css';

const FADE_DURATION = 150;

type SinglePageProps = {
    type: 'singlePage';
    bodyId?: string;
    bodyComponent: React.ReactNode | null | undefined;
    artImage: React.ReactElement<any> | null | undefined;
    artBackgroundColor: string;
    artBackgroundGradient?: string;
    buttonText: string; // Optional,
    primaryAction?: {
        href?: string;
        onClick?: () => void;
    };
    onClose: () => void;
    bottomLeftContent?: React.ReactNode;
    primaryActionDisabled?: boolean;
    actionButtonOnRight?: boolean;
    secondaryButtonDisabled?: boolean; // Optional,
    secondaryAction?: {
        onClick: () => void;
        buttonText: string;
    };
};

type MultiPageProps = {
    type: 'multiPage';
    bodyComponent: React.ReactNode | null | undefined;
    bodyId?: string;
    artImage: React.ReactElement<any> | null | undefined;
    artBackgroundColor: string;
    artBackgroundGradient?: string;
    buttonText: string; // Optional,
    nextButtonDisabled?: boolean; // Optional,
    secondaryButtonDisabled?: boolean; // Optional,
    finalButtonText: string; // Optional,
    finalButtonDisabled?: boolean; // Optional,
    secondaryButtonText?: string; // Optional,
    currentPage: number; // Optional,
    pageCount: number;
    onNext: () => void;
    onGotoPage: (arg1: number) => void;
    onClose: () => void;
    onFinish: () => void;
    onClickSecondaryButton?: () => void;
    // Hack to allow the content of the page to be full width – usually
    // used for stuff like loom videos, etc. Will prevent animation to/from
    // this specific page.
    isFullWidth?: boolean;
};

type Props = SinglePageProps | MultiPageProps;

type State = {
    isBodyFadingOut: boolean;
    bodyComponent: React.ReactNode | null | undefined;
};

/**
This component is typically rendered inside a `<Modal>` component,
and is used to present information to the user in a modal.  It can be
a single page, or include controlls for interacting with multiple pages.
This component itself only takes a single `artImage` and `bodyComponent`,
so if you want to use multiple pages, it's up to the parent to control
those props and change them as needed.
*/
export class OnboardingPage extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isBodyFadingOut: false,
            bodyComponent: props.bodyComponent,
        };
    }

    static defaultProps = {
        buttonText: 'Next',
        finalButtonText: 'Finish',
        currentPage: 0,
    };

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            (nextProps.bodyComponent !== this.props.bodyComponent && !nextProps.bodyId) ||
            // Because we're now passing in rendered components, and not
            // components to be rendered, the above check is always true. This
            // causes a fade far too often. Checking the body id value fixes
            // that
            (this.props.type === 'multiPage' &&
                nextProps.bodyId &&
                this.props.bodyId !== nextProps.bodyId &&
                // @ts-expect-error
                !nextProps.isFullWidth &&
                !this.props.isFullWidth)
        ) {
            this.setState({isBodyFadingOut: true});
            setTimeout(() => {
                this.setState({
                    isBodyFadingOut: false,
                    bodyComponent: nextProps.bodyComponent,
                });
            }, FADE_DURATION);
        } else if (nextProps.bodyComponent !== this.props.bodyComponent) {
            // This allows us to re-render the body component without the fade
            // if body id does not change but the prop changes
            this.setState({bodyComponent: nextProps.bodyComponent});
        }
    }

    render() {
        const body = this.state.bodyComponent;
        const bodyStyleName = classnames(styles.body, {
            [styles['is-fading-out']]: this.state.isBodyFadingOut,
        });

        let primaryButton = (
            <Button
                variant='primary'
                autoFocus={true}
                onClick={this.handlePrimaryButtonClick}
                // @ts-expect-error
                disabled={this.props.primaryActionDisabled || false}
            >
                {this.props.buttonText}
            </Button>
        );
        const isLastPage =
            this.props.type === 'multiPage' && this.props.currentPage === this.props.pageCount - 1;
        if (this.props.type === 'multiPage') {
            if (isLastPage) {
                primaryButton = (
                    <Button
                        variant='primary'
                        onClick={this.handlePrimaryButtonClick}
                        disabled={this.props.finalButtonDisabled}
                    >
                        {this.props.finalButtonText}
                    </Button>
                );
            } else {
                primaryButton = (
                    <Button
                        variant='primary'
                        onClick={this.handlePrimaryButtonClick}
                        disabled={this.props.nextButtonDisabled}
                    >
                        {this.props.buttonText}
                    </Button>
                );
            }
        }
        // @ts-expect-error
        if (this.props.primaryAction && typeof this.props.primaryAction.href === 'string') {
            primaryButton = (
                // @ts-expect-error
                <Link href={this.props.primaryAction.href} size='small' variant='primary'>
                    {this.props.buttonText}
                </Link>
            );
        }

        let secondaryButton;
        if (this.props.type === 'multiPage' && this.props.currentPage >= 1) {
            secondaryButton = (
                <Button
                    variant='text-secondary'
                    onClick={this.handleSecondaryButtonClick}
                    disabled={this.props.secondaryButtonDisabled}
                >
                    {this.props.secondaryButtonText || 'Back'}
                </Button>
            );
        }
        if (this.props.type === 'singlePage' && this.props.primaryAction && this.props.onClose) {
            if (this.props.secondaryAction) {
                secondaryButton = (
                    <Button
                        variant='text-secondary'
                        onClick={this.props.secondaryAction.onClick}
                        disabled={this.props.secondaryButtonDisabled}
                    >
                        {this.props.secondaryAction.buttonText}
                    </Button>
                );
            } else {
                secondaryButton = (
                    <Button
                        variant='text-secondary'
                        onClick={this.props.onClose}
                        disabled={this.props.secondaryButtonDisabled}
                    >
                        Close
                    </Button>
                );
            }
        }

        return (
            <div className={styles['onboarding-page']}>
                {/* @ts-expect-error */}
                {!this.props.isFullWidth && (
                    <div
                        className={styles.left}
                        style={
                            this.props.artBackgroundGradient
                                ? {background: this.props.artBackgroundGradient}
                                : {backgroundColor: this.props.artBackgroundColor}
                        }
                    >
                        {this.props.artImage}
                    </div>
                )}
                <div
                    // @ts-expect-error
                    style={this.props.isFullWidth ? {width: '100%'} : undefined}
                    className={styles.right}
                >
                    {body ? <div className={bodyStyleName}>{body}</div> : null}
                    <div className={styles.controls}>
                        {this.props.type === 'multiPage' ? (
                            <SlideshowControls
                                slideCount={this.props.pageCount}
                                selectedSlide={this.props.currentPage}
                                onControlClick={this.props.onGotoPage}
                            />
                        ) : (
                            this.props.bottomLeftContent
                        )}
                        <div
                            className={
                                // @ts-expect-error
                                this.props.actionButtonOnRight
                                    ? styles['links--spaced']
                                    : styles.links
                            }
                        >
                            {secondaryButton}
                            {primaryButton}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    handlePrimaryButtonClick = () => {
        if (this.props.type === 'multiPage') {
            if (this.props.currentPage === this.props.pageCount - 1) {
                // Final page
                this.props.onFinish();
            } else {
                this.props.onNext();
            }
        } else if (this.props.primaryAction && this.props.primaryAction.onClick) {
            this.props.primaryAction.onClick();
        } else {
            this.props.onClose();
        }
    };

    handleSecondaryButtonClick = () => {
        if (this.props.type === 'multiPage' && this.props.onClickSecondaryButton) {
            this.props.onClickSecondaryButton();
        }
        if (this.props.type === 'multiPage' && this.props.currentPage >= 1) {
            this.props.onGotoPage(this.props.currentPage - 1);
        }
    };
}
